import React, { Component } from 'react'
import { RouteComponentProps } from 'react-router-dom'
import { WithTranslation } from 'react-i18next'

import defaultTo from 'lodash/defaultTo'
import isNil from 'lodash/isNil'
import get from 'lodash/get'
import pick from 'lodash/pick'

import PageLayout from 'components/layout/pageLayout'
import Head from 'components/layout/head'
import Radio from 'components/ui/radio'
import TextArea from 'components/ui/textArea'
import Input from 'components/ui/input'
import Button from 'components/ui/button'

interface FeedbackProps extends RouteComponentProps<any>, WithTranslation {
  authenticated: boolean
  sendFeedback: any
}

interface IErrorObject {
  [k: string]: string | null
}

type FeedbackState = {
  errors: IErrorObject | null
  success: boolean
  rating: string | null
  mainReason: string | null
  email: string | null
  suggestions: string | null
}

class Feedback extends Component<FeedbackProps, FeedbackState> {
  constructor(props: FeedbackProps) {
    super(props)
    this.state = {
      errors: null,
      success: false,
      ...this.formFields,
    }
  }

  formFields = {
    rating: null,
    mainReason: null,
    email: null,
    suggestions: null,
  }

  getValue = (key: string) => {
    const stateValue = get(this.state, key)

    if (!isNil(stateValue)) {
      return stateValue
    }

    return undefined
  }

  isEmpty = (value: string) => isNil(value) || value === ''

  getVariables = () => {
    const formFields = Object.keys(this.formFields)
    const variables = this.state

    // Filter out non form fields from state object
    return pick(variables, formFields)
  }

  handleSubmit = () => {
    const { sendFeedback: mutate } = this.props

    const errors = {
      mainReason: this.isEmpty(this.getValue('mainReason')) ? 'Error Message.' : null,
    }

    const hasErrors = Object.values(errors).find((error) => {
      if (error) return true
      return false
    })

    if (hasErrors) {
      this.setState({ errors, success: false })
    } else {
      if (mutate) {
        mutate({
          variables: { ...this.getVariables() },
        })
          .then((res: any) => {
            // Feedback was send successfully
            if (get(res, 'data.sendFeedback')) {
              // Show success message and reset the form fields
              this.setState({
                errors: null,
                success: true,
                ...this.formFields,
              })
            } else {
              this.setState({
                errors: {
                  api: 'Error Message',
                },
                success: false,
              })
            }
          })
          .catch((err: any) => {
            this.setState({
              errors: {
                api: 'Error Message',
              },
              success: false,
            })
          })
      }
    }
  }

  render() {
    const { t } = this.props
    const { errors, success } = this.state

    return (
      <PageLayout pageTitle="Feedback">
        <Head
          headline="Feedback"
          subheadline={
            process.env.REACT_APP_ENV === 'rigips' ? t('appNameRigips') : t('appNameAbk')
          }
          error={errors ? true : false}
          message={
            errors ? 'Pflichtfelder ausfüllen!' : success ? 'Feedback wurde gesendet.' : undefined
          }
        />

        <div className="px-48 my-65">
          <p className="font-400 text-15 text-black tracking-0.1 leading-22">
            {process.env.REACT_APP_ENV === 'rigips'
              ? t('feedbackTextRigips')
              : t('feedbackTextAbk')}
          </p>
          <div className="mt-45">
            <Radio
              underline
              label="Wie bewerten Sie das Programm?"
              value={defaultTo(this.getValue('rating'), '')}
              options={[
                { label: 'Sehr gut', value: '1' },
                { label: 'Gut', value: '2' },
                { label: 'Weniger gut', value: '3' },
                { label: 'Schlecht', value: '4' },
              ]}
              error={errors && errors.rating ? errors.rating : undefined}
              onChange={(value) => this.setState({ rating: value ? String(value) : null })}
            />
          </div>
          <div className="mt-45">
            <Radio
              underline
              label="Was ist der Hauptgrund für Ihr Feedbak?"
              value={defaultTo(this.getValue('mainReason'), '')}
              options={[
                { label: 'Lob / Kritik', value: 'praiseOrCriticism' },
                { label: 'Optimierungsvorschlag', value: 'optimizationSuggestion' },
                { label: 'Fehlermeldung', value: 'errorMessage' },
                { label: 'Sonstiges', value: 'other' },
              ]}
              error={errors && errors.mainReason ? errors.mainReason : undefined}
              onChange={(value) => this.setState({ mainReason: value ? String(value) : null })}
            />
          </div>
          <div className="mt-45">
            <TextArea
              label="Anmerkungen"
              value={defaultTo(this.getValue('suggestions'), '')}
              error={errors && errors.annotations ? errors.annotations : undefined}
              onChange={(value) => this.setState({ suggestions: value ? String(value) : null })}
            />
          </div>
          <div className="mt-45 flex">
            <div className="flex w-6/12 items-end">
              <p className="font-400 text-15 text-black tracking-0.1 leading-22">
                Mit dem Absenden stimmen Sie der Verarbeitung Ihrer angegebenen Daten für das
                Bearbeiten Ihrer Anfrage zu.{' '}
                <a
                  className="link text-15"
                  target="_blank"
                  rel="noopener noreferrer"
                  href={
                    process.env.REACT_APP_ENV === 'rigips'
                      ? 'https://www.rigips.at/impressum'
                      : 'https://www.abk.at/datenschutz/'
                  }
                >
                  Datenschutzerklärung und Widerrufshinweise
                </a>
                .
              </p>
            </div>
            <div className="flex w-6/12 ml-30 items-end">
              <Input
                type="email"
                name="email"
                label="E-Mail"
                leftAlign
                value={defaultTo(this.getValue('email'), '')}
                error={errors && errors.email ? errors.email : undefined}
                onChange={(value) => this.setState({ email: value ? String(value) : null })}
              />
              <Button className="ml-54.5" primary onClick={this.handleSubmit}>
                Absenden
              </Button>
            </div>
          </div>
        </div>
      </PageLayout>
    )
  }
}

export default Feedback
